import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  accountingChartsApi,
  accountingJournalsApi,
  accountingJournalDocumentsApi,
  type AccountDto,
  type JournalEntryDto,
  type JournalEntryDocumentDto,
} from '../../services/api';

const CURRENCY = 'PHP';
const CURRENCY_SYMBOL = '₱';

function formatPeso(value: number): string {
  return new Intl.NumberFormat('en-PH', { style: 'currency', currency: CURRENCY }).format(value);
}

/** Display model: one line = one leg, either debit or credit */
export interface JournalEntryLineDisplay {
  id: string;
  account: string;
  accountCode?: string;
  accountName?: string;
  description: string;
  type: 'debit' | 'credit';
  amount: number;
}

/** API/backend model: each line has both debit and credit (one is 0) */
export interface JournalEntryLine {
  id: string;
  account: string;
  accountCode?: string;
  accountName?: string;
  description: string;
  debit: number;
  credit: number;
}

export interface JournalEntry {
  id: string;
  entryNumber: string;
  date: string;
  reference: string;
  description: string;
  lines: JournalEntryLine[];
  status: 'Draft' | 'Posted';
}

function getNextEntryNumber(entries: JournalEntry[]): string {
  const max = entries.reduce((m, e) => {
    const n = parseInt(e.entryNumber.replace(/\D/g, ''), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, 0);
  return `JE-${String(max + 1).padStart(4, '0')}`;
}

function formatDateForInput(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface AccountOption {
  id: string;
  code: string;
  name: string;
}

interface CreateJournalEntryModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (entry: Omit<JournalEntry, 'id' | 'entryNumber'> & { entryNumber: string }) => Promise<boolean>;
  nextEntryNumber: string;
  accountOptions: AccountOption[];
  submitting: boolean;
  initialEntry?: JournalEntry | null;
  entryId?: string | null;
}

function CreateJournalEntryModal({
  open,
  onClose,
  onSubmit,
  nextEntryNumber,
  accountOptions,
  submitting,
  initialEntry,
  entryId,
}: CreateJournalEntryModalProps) {
  const today = useMemo(() => formatDateForInput(new Date().toISOString()), []);
  const [date, setDate] = useState(today);
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<JournalEntryLineDisplay[]>([
    { id: crypto.randomUUID(), account: '', description: '', type: 'debit', amount: 0 },
    { id: crypto.randomUUID(), account: '', description: '', type: 'credit', amount: 0 },
  ]);

  const isEdit = Boolean(entryId && initialEntry);

  useEffect(() => {
    if (open) {
      if (initialEntry) {
        setDate(formatDateForInput(initialEntry.date));
        setReference(initialEntry.reference || '');
        setDescription(initialEntry.description || '');
        setLines(
          initialEntry.lines.length > 0
            ? initialEntry.lines.map((l) => ({
                id: l.id,
                account: l.account,
                accountCode: l.accountCode,
                accountName: l.accountName,
                description: l.description || '',
                type: (l.debit && Number(l.debit) > 0 ? 'debit' : 'credit') as 'debit' | 'credit',
                amount: Number(l.debit) > 0 ? Number(l.debit) : Number(l.credit) || 0,
              }))
            : [
                { id: crypto.randomUUID(), account: '', description: '', type: 'debit', amount: 0 },
                { id: crypto.randomUUID(), account: '', description: '', type: 'credit', amount: 0 },
              ]
        );
      } else {
        setDate(formatDateForInput(new Date().toISOString()));
        setReference('');
        setDescription('');
        setLines([
          { id: crypto.randomUUID(), account: '', description: '', type: 'debit', amount: 0 },
          { id: crypto.randomUUID(), account: '', description: '', type: 'credit', amount: 0 },
        ]);
      }
    }
  }, [open, initialEntry]);

  const totalDebit = useMemo(
    () => lines.reduce((s, l) => s + (l.type === 'debit' && Number.isFinite(l.amount) ? l.amount : 0), 0),
    [lines]
  );
  const totalCredit = useMemo(
    () => lines.reduce((s, l) => s + (l.type === 'credit' && Number.isFinite(l.amount) ? l.amount : 0), 0),
    [lines]
  );
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
  const discrepancy = totalDebit - totalCredit;

  const updateLine = (id: string, field: keyof JournalEntryLineDisplay, value: string | number) => {
    setLines((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l))
    );
  };

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { id: crypto.randomUUID(), account: '', description: '', type: 'debit', amount: 0 },
    ]);
  };

  const removeLine = (id: string) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((l) => l.id !== id));
  };

  const toApiLines = (): JournalEntryLine[] =>
    lines
      .filter((l) => l.account && Number(l.amount) > 0)
      .map((l) => ({
        id: l.id,
        account: l.account,
        accountCode: l.accountCode,
        accountName: l.accountName,
        description: l.description,
        debit: l.type === 'debit' ? (Number.isFinite(l.amount) ? l.amount : 0) : 0,
        credit: l.type === 'credit' ? (Number.isFinite(l.amount) ? l.amount : 0) : 0,
      }));

  const displayEntryNumber = isEdit && initialEntry ? initialEntry.entryNumber : nextEntryNumber;

  const handleSaveDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await onSubmit({
      entryNumber: displayEntryNumber,
      date: date || today,
      reference: reference.trim(),
      description: description.trim(),
      lines: toApiLines(),
      status: 'Draft',
    });
    if (success) onClose();
  };

  const handlePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isBalanced) return;
    const success = await onSubmit({
      entryNumber: displayEntryNumber,
      date: date || today,
      reference: reference.trim(),
      description: description.trim(),
      lines: toApiLines(),
      status: 'Posted',
    });
    if (success) onClose();
  };

  const balanceHint = !isBalanced && Math.abs(discrepancy) >= 0.01
    ? (discrepancy > 0
        ? `Add credit of ${formatPeso(discrepancy)} to balance, or post as draft.`
        : `Add debit of ${formatPeso(-discrepancy)} to balance and enable Post Entry.`)
    : null;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="shrink-0 border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit Journal Entry' : 'Create Journal Entry'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form className="flex-1 min-h-0 flex flex-col overflow-hidden" onSubmit={(e) => e.preventDefault()}>
          <div className="p-5 space-y-3 shrink-0 border-b border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entry Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50/80 pl-3 pr-9 py-2 text-sm text-gray-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none"
                  />
                  <svg
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="e.g., INV-001"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Entry description"
                className="w-full rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:bg-white outline-none"
              />
            </div>
          </div>

          <div className="p-5 flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-sm font-medium text-gray-700">Entry lines</span>
              <span className="text-xs text-gray-500">Each line is debit only or credit only</span>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">
                <div className="col-span-4">Account</div>
                <div className="col-span-3">Description</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Amount</div>
                <div className="col-span-1" />
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                {lines.map((line) => (
                  <div
                    key={line.id}
                    className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-gray-100 items-center"
                  >
                    <div className="col-span-4">
                      <select
                        value={line.account}
                        onChange={(e) => updateLine(line.id, 'account', e.target.value)}
                        className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 outline-none"
                      >
                        <option value="">Select account</option>
                        {accountOptions.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} – {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-3">
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                        placeholder="Line description"
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm placeholder-gray-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 outline-none"
                      />
                    </div>
                    <div className="col-span-2">
                      <select
                        value={line.type}
                        onChange={(e) => updateLine(line.id, 'type', e.target.value as 'debit' | 'credit')}
                        className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 outline-none"
                      >
                        <option value="debit">Debit</option>
                        <option value="credit">Credit</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        value={line.amount === 0 ? '' : line.amount}
                        onChange={(e) => {
                          const v = e.target.value ? parseFloat(e.target.value) : 0;
                          updateLine(line.id, 'amount', v);
                        }}
                        placeholder={line.type === 'debit' ? 'Debit amount' : 'Credit amount'}
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm text-right tabular-nums focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 outline-none"
                      />
                    </div>
                    <div className="col-span-1">
                      {lines.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLine(line.id)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                          aria-label="Remove line"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-3 py-3 border-t border-gray-200 bg-gray-50 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={addLine}
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Line
                  </button>
                  <div className="flex items-center gap-4 text-sm flex-wrap">
                    <span className="text-gray-600">
                      Debit total: <strong className="tabular-nums text-gray-900">{formatPeso(totalDebit)}</strong>
                    </span>
                    <span className="text-gray-600">
                      Credit total: <strong className="tabular-nums text-gray-900">{formatPeso(totalCredit)}</strong>
                    </span>
                    {isBalanced ? (
                      <span className="text-emerald-600 font-medium tabular-nums">✓ Balanced</span>
                    ) : (
                      <span className="text-red-600 font-medium tabular-nums" title="Accounting discrepancy: debits do not equal credits">
                        Discrepancy: {formatPeso(Math.abs(discrepancy))}
                      </span>
                    )}
                  </div>
                </div>
                {balanceHint && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    {balanceHint}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-gray-100 px-5 py-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={submitting}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save as Draft'}
            </button>
            <button
              type="button"
              onClick={handlePost}
              disabled={!isBalanced || submitting}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Posting…' : 'Post Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface ViewJournalEntryModalProps {
  entry: JournalEntry;
  accountOptions: AccountOption[];
  onClose: () => void;
}

function ViewJournalEntryModal({ entry, accountOptions, onClose }: ViewJournalEntryModalProps) {
  const dateDisplay = entry.date
    ? new Date(entry.date).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';
  const accountLabel = (accountId: string) => {
    const a = accountOptions.find((o) => o.id === accountId);
    return a ? `${a.code} – ${a.name}` : accountId;
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="shrink-0 border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">View Journal Entry</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Entry #</span>
              <p className="font-medium text-gray-900">{entry.entryNumber}</p>
            </div>
            <div>
              <span className="text-gray-500">Date</span>
              <p className="font-medium text-gray-900">{dateDisplay}</p>
            </div>
            <div>
              <span className="text-gray-500">Reference</span>
              <p className="font-medium text-gray-900">{entry.reference || '—'}</p>
            </div>
            <div>
              <span className="text-gray-500">Status</span>
              <p className="font-medium">
                <span
                  className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    entry.status === 'Posted' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {entry.status}
                </span>
              </p>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Entry lines</div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Account</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Type</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entry.lines.map((line) => (
                    <tr key={line.id}>
                      <td className="px-3 py-2 text-gray-900">
                        {line.accountCode && line.accountName
                          ? `${line.accountCode} – ${line.accountName}`
                          : accountLabel(line.account)}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {Number(line.debit) > 0 ? 'Debit' : 'Credit'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                        {formatPeso(Number(line.debit) > 0 ? line.debit : line.credit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="shrink-0 border-t border-gray-100 px-5 py-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function dtoToEntry(d: JournalEntryDto): JournalEntry {
  return {
    id: d.id,
    entryNumber: d.entryNumber,
    date: d.date,
    reference: d.reference,
    description: d.description,
    status: d.status as 'Draft' | 'Posted',
    lines: d.lines.map((l) => ({
      id: l.id,
      account: l.account,
      accountCode: l.accountCode,
      accountName: l.accountName,
      description: l.description,
      debit: l.debit,
      credit: l.credit,
    })),
  };
}

export default function JournalEntriesPage() {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [viewEntry, setViewEntry] = useState<JournalEntry | null>(null);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [documentsModal, setDocumentsModal] = useState<{ entryId: string; entryNumber: string } | null>(null);
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionMenuKey, setActionMenuKey] = useState<string | null>(null);
  const actionMenuRef = useRef<HTMLTableCellElement>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await accountingChartsApi.list();
      if (res.data) {
        setAccountOptions(res.data.map((a: AccountDto) => ({ id: a.id, code: a.code, name: a.name })));
      } else {
        setAccountOptions([]);
      }
    } catch {
      setAccountOptions([]);
    }
  }, []);

  const fetchJournals = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await accountingJournalsApi.list();
      if (res.data) {
        setEntries(res.data.map(dtoToEntry));
      } else {
        setEntries([]);
      }
    } catch {
      setApiError('Failed to load journal entries.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    fetchJournals();
  }, [fetchJournals]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setActionMenuKey(null);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const nextEntryNumber = getNextEntryNumber(entries);

  const filteredEntries = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.trim().toLowerCase();
    return entries.filter(
      (e) =>
        e.entryNumber.toLowerCase().includes(q) ||
        (e.reference && e.reference.toLowerCase().includes(q)) ||
        (e.description && e.description.toLowerCase().includes(q))
    );
  }, [entries, search]);

  /** One row per debit/credit line so debit and credit are on separate rows */
  const listingRows = useMemo(() => {
    const rows: { entry: JournalEntry; line: JournalEntryLine }[] = [];
    filteredEntries.forEach((entry) => {
      entry.lines.forEach((line) => {
        if (Number(line.debit) > 0 || Number(line.credit) > 0) {
          rows.push({ entry, line });
        }
      });
    });
    return rows;
  }, [filteredEntries]);

  const handleSubmit = async (entry: Omit<JournalEntry, 'id'> & { entryNumber: string }): Promise<boolean> => {
    setApiError(null);
    const payload = {
      entry_number: entry.entryNumber,
      date: entry.date,
      reference: entry.reference || '',
      description: entry.description || '',
      status: entry.status,
      lines: entry.lines
        .filter((l) => l.account && (Number(l.debit) > 0 || Number(l.credit) > 0))
        .map((l) => ({
          account_id: l.account,
          description: l.description || '',
          debit: Number.isFinite(l.debit) ? l.debit : 0,
          credit: Number.isFinite(l.credit) ? l.credit : 0,
        })),
    };
    if (payload.lines.length === 0) {
      setApiError('At least one line with debit or credit is required.');
      return false;
    }
    setSubmitting(true);
    try {
      const res = await accountingJournalsApi.create(payload);
      if (res.data) {
        setEntries((prev) => [dtoToEntry(res.data!), ...prev]);
        setModalOpen(false);
        return true;
      }
      setApiError(res.message || 'Failed to create journal entry.');
      return false;
    } catch {
      setApiError('Failed to create journal entry.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (entry: Omit<JournalEntry, 'id'> & { entryNumber: string }): Promise<boolean> => {
    if (!editingEntry) return false;
    setApiError(null);
    const payload = {
      entry_number: entry.entryNumber,
      date: entry.date,
      reference: entry.reference || '',
      description: entry.description || '',
      status: entry.status,
      lines: entry.lines
        .filter((l) => l.account && (Number(l.debit) > 0 || Number(l.credit) > 0))
        .map((l) => ({
          account_id: l.account,
          description: l.description || '',
          debit: Number.isFinite(l.debit) ? l.debit : 0,
          credit: Number.isFinite(l.credit) ? l.credit : 0,
        })),
    };
    if (payload.lines.length === 0) {
      setApiError('At least one line with debit or credit is required.');
      return false;
    }
    setSubmitting(true);
    try {
      const res = await accountingJournalsApi.update(editingEntry.id, payload);
      if (res.data) {
        setEntries((prev) => prev.map((e) => (e.id === editingEntry.id ? dtoToEntry(res.data!) : e)));
        setEditingEntry(null);
        return true;
      }
      setApiError(res.message || 'Failed to update journal entry.');
      return false;
    } catch {
      setApiError('Failed to update journal entry.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entry: JournalEntry) => {
    if (!window.confirm(`Delete journal entry ${entry.entryNumber}? This cannot be undone.`)) return;
    setApiError(null);
    try {
      await accountingJournalsApi.delete(entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    } catch {
      setApiError('Failed to delete journal entry.');
    }
  };

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Journal Entries</h1>
          <p className="text-sm text-gray-500 mt-0.5">Record and manage manual journal entries</p>
        </div>
        <button
          type="button"
          onClick={() => { setApiError(null); setEditingEntry(null); setModalOpen(true); }}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Entry
        </button>
      </div>

      {apiError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 flex items-center justify-between">
          <span>{apiError}</span>
          <button type="button" onClick={() => setApiError(null)} className="text-red-600 hover:text-red-800" aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="relative mb-4">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search entries..."
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none text-sm"
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-card flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500 flex-1">
            <p className="font-medium text-gray-600">Loading journal entries…</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500 flex-1">
            <span className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </span>
            <p className="font-medium text-gray-600">No journal entries found.</p>
            <p className="text-sm mt-1 max-w-sm">
              {entries.length === 0
                ? 'Create your first journal entry using the button above.'
                : 'No entries match your search.'}
            </p>
            {entries.length === 0 && (
              <button
                type="button"
                onClick={() => { setApiError(null); setEditingEntry(null); setModalOpen(true); }}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Entry
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto flex-1 min-h-0">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Entry #
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Date
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Reference
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Account
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Debit
                  </th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Credit
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Status
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-24">
                    Documents
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-28">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {listingRows.map(({ entry, line }) => {
                  const dateDisplay = entry.date
                    ? new Date(entry.date).toLocaleDateString('en-PH', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })
                    : '—';
                  const acc = accountOptions.find((a) => a.id === line.account);
                  const accountDisplay = line.accountCode && line.accountName
                    ? `${line.accountCode} – ${line.accountName}`
                    : acc ? `${acc.code} – ${acc.name}` : line.account;
                  const isDebit = Number(line.debit) > 0;
                  const amount = isDebit ? Number(line.debit) : Number(line.credit);
                  return (
                    <tr key={`${entry.id}-${line.id}`} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{entry.entryNumber}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{dateDisplay}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{entry.reference || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                        {accountDisplay}
                      </td>
                      <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700">
                        {isDebit ? formatPeso(amount) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-700">
                        {!isDebit ? formatPeso(amount) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            entry.status === 'Posted'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setDocumentsModal({ entryId: entry.id, entryNumber: entry.entryNumber })}
                          className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 text-sm font-medium"
                          title="Manage documents"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Documents
                        </button>
                      </td>
                      <td className="px-4 py-3 relative" ref={actionMenuKey === `${entry.id}-${line.id}` ? actionMenuRef : undefined}>
                        <div className="flex items-center justify-center">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setActionMenuKey(actionMenuKey === `${entry.id}-${line.id}` ? null : `${entry.id}-${line.id}`); }}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                            aria-label="Actions"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                            </svg>
                          </button>
                          {actionMenuKey === `${entry.id}-${line.id}` && (
                            <div className="absolute right-4 top-full mt-1 w-36 rounded-lg border border-gray-200 bg-white py-1 shadow-lg z-10">
                              <button
                                type="button"
                                onClick={() => { setActionMenuKey(null); setViewEntry(entry); }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                              </button>
                              <button
                                type="button"
                                onClick={() => { setActionMenuKey(null); setEditingEntry(entry); }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Update
                              </button>
                              <button
                                type="button"
                                onClick={() => { setActionMenuKey(null); handleDeleteEntry(entry); }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateJournalEntryModal
        open={modalOpen || Boolean(editingEntry)}
        onClose={() => { setModalOpen(false); setEditingEntry(null); }}
        onSubmit={editingEntry ? handleUpdate : handleSubmit}
        nextEntryNumber={editingEntry ? editingEntry.entryNumber : nextEntryNumber}
        accountOptions={accountOptions}
        submitting={submitting}
        initialEntry={editingEntry}
        entryId={editingEntry?.id}
      />

      {viewEntry && (
        <ViewJournalEntryModal
          entry={viewEntry}
          accountOptions={accountOptions}
          onClose={() => setViewEntry(null)}
        />
      )}

      {documentsModal && (
        <DocumentsModal
          entryId={documentsModal.entryId}
          entryNumber={documentsModal.entryNumber}
          onClose={() => setDocumentsModal(null)}
        />
      )}
    </div>
  );
}

interface DocumentsModalProps {
  entryId: string;
  entryNumber: string;
  onClose: () => void;
}

function DocumentsModal({ entryId, entryNumber, onClose }: DocumentsModalProps) {
  const [list, setList] = useState<JournalEntryDocumentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    accountingJournalDocumentsApi.list(entryId).then((res) => {
      if (cancelled) return;
      if (res.data) setList(res.data);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [entryId]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await accountingJournalDocumentsApi.upload(entryId, file);
      if (res.data) setList((prev) => [...prev, res.data!]);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = (doc: JournalEntryDocumentDto) => {
    accountingJournalDocumentsApi.download(doc.id, doc.name).catch(() => {});
  };

  const handleDelete = async (doc: JournalEntryDocumentDto) => {
    await accountingJournalDocumentsApi.delete(doc.id);
    setList((prev) => prev.filter((d) => d.id !== doc.id));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Documents — {entryNumber}</h3>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Close">×</button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-gray-500">No documents attached. Add one below.</p>
          ) : (
            <ul className="space-y-2">
              {list.map((doc) => (
                <li key={doc.id} className="flex items-center gap-2 py-2 border-b border-gray-100 last:border-0">
                  <span className="flex-1 truncate text-sm text-gray-700" title={doc.name}>{doc.name}</span>
                  <button type="button" onClick={() => handleDownload(doc)} className="text-sm font-medium text-brand-600 hover:text-brand-700">Download</button>
                  <button type="button" onClick={() => handleDelete(doc)} className="text-sm font-medium text-red-600 hover:text-red-700">Delete</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-200">
          <label className="inline-flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 cursor-pointer">
            <input type="file" className="hidden" onChange={handleFileChange} disabled={uploading} />
            {uploading ? 'Uploading…' : 'Add document'}
          </label>
        </div>
      </div>
    </div>
  );
}
